import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// The return page NEVER grants access — it only reads status from our DB.
// The webhook is the sole activator; it may lag a few seconds, so we poll.
export function PaymentSuccess() {
  const { t } = useI18n();
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState("activating");

  useEffect(() => {
    if (!sessionId) {
      setState("timeout");
      return undefined;
    }
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          clearInterval(id);
          return;
        }
      } catch { /* keep polling */ }
      if (tries >= 15) {
        setState("timeout");
        clearInterval(id);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [sessionId]);

  return (
    <div className="flex justify-center pt-20" data-testid="payment-success-page">
      <div className="hud-card w-full max-w-md rounded-xl p-8 text-center">
        {state === "activating" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#00F0FF]" data-testid="payment-activating-icon" />
            <h1 className="mt-4 font-display text-2xl font-bold uppercase text-white">{t("pay.activating")}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#00FF87]" data-testid="payment-success-icon" />
            <h1 className="mt-4 font-display text-2xl font-bold uppercase text-white">{t("premium.success_title")}</h1>
            <p className="mt-2 text-sm text-slate-400">{t("premium.success_body")}</p>
            <Link to="/dashboard" className="btn-volt mt-6" data-testid="payment-success-dashboard">{t("nav.dashboard")}</Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <Clock className="mx-auto h-10 w-10 text-[#FFD600]" data-testid="payment-timeout-icon" />
            <h1 className="mt-4 font-display text-2xl font-bold uppercase text-white">{t("pay.taking_longer")}</h1>
            <p className="mt-2 text-sm text-slate-400">{t("pay.taking_longer_body")}</p>
            <Link to="/" className="btn-volt mt-6">{t("nav.watchlist")}</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  return (
    <div className="flex justify-center pt-20" data-testid="payment-cancel-page">
      <div className="hud-card w-full max-w-md rounded-xl p-8 text-center">
        <XCircle className="mx-auto h-10 w-10 text-slate-500" />
        <p className="mt-4 text-sm text-slate-300" data-testid="payment-cancel-body">{t("pay.cancelled_body")}</p>
        <Link to="/pricing" className="btn-volt mt-6" data-testid="payment-cancel-pricing">{t("nav.premium")}</Link>
      </div>
    </div>
  );
}
